/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'

type BalanceHeaderPanelProps = {
  /**
   * Where the team stands: the standing banner for one registration, or the
   * list of status pills on the All-Divisions view.
   */
  header?: ReactNode
  /** The payment milestones. Absent on All Divisions and without a status. */
  timeline?: ReactNode
  /** The money figures. */
  moneyMeter?: ReactNode
}

/**
 * SN-4709 (Inon, Aug 12) — one header panel, laid out by what each part is FOR.
 *
 * The header used to be four things at three rhythms: an uncarded roster
 * selector with a status pill floating beside it, a bordered timeline card
 * starting at 45% of the row, and two more cards below split at 49%. Nothing
 * shared a left edge, a divider or a height.
 *
 * SN-4709 (Inon, Sep 2) — THE RAIL IS GONE, and with it the two-column grid.
 *
 * The rail existed to hold the roster picker, at a fixed 440px because
 * `RosterSelectNew` hardcoded a 400px width. That was ~30% of the panel spent on
 * a control that scopes the whole TAB, not this panel — so the picker moved up to
 * the Registration tab's scope row, beside the sub-tabs it also governs.
 *
 * What is left is a single column of full-width regions, seamed. Every region now
 * starts on the same left edge and gets the entire width, which is what the
 * runway had been short of.
 *
 * SN-8310 (Sep 22) — the regions are now WHERE THE TEAM STANDS (a sentence),
 * WHEN (the payment milestones) and HOW MUCH (the figures), in that order.
 *
 * Composition-only: it owns the chrome and the seams, nothing else. The pieces
 * stay their own components and keep their own tests.
 */
const BalanceHeaderPanel = ({
  header,
  timeline,
  moneyMeter,
}: BalanceHeaderPanelProps) => (
  <div css={panelStyle} data-testid='balance-header-panel'>
    {header ? <div css={regionStyle}>{header}</div> : null}
    {timeline ? (
      <div css={[regionStyle, header ? seamStyle : undefined]}>{timeline}</div>
    ) : null}
    {moneyMeter ? (
      <div css={[regionStyle, header || timeline ? seamStyle : undefined]}>
        {moneyMeter}
      </div>
    ) : null}
  </div>
)

export default BalanceHeaderPanel

/**
 * The same raised SECONDARY_300 surface with a SECONDARY_100 edge the sheet
 * below uses — one border for the whole header, rather than one per part.
 */
const panelStyle = css`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background: ${colors.SECONDARY_300};
  /* Keeps the region seams from painting over the rounded corners. */
  overflow: hidden;
`

const regionStyle = css`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
  padding: 16px;
`

/** The seam between regions — declared on the LOWER one, so the first has none. */
const seamStyle = css`
  border-top: 1px solid ${colors.SECONDARY_100};
`
